// neurotrader-admin/src/pages/ClientsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './ClientsPage.css';

const ClientsPage = () => {
    const [clients, setClients] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalClients: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // --- NOVO: ESTADO PARA ORDENAÇÃO ---
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'DESC' });

    const { user } = useAuth();

    const fetchClients = useCallback(async (page = 1, search = '', sort = {}) => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const { data } = await api.get('/api/admin/clients', {
                headers: { Authorization: `Bearer ${user.token}` },
                params: {
                    page,
                    search,
                    limit: 15,
                    sortBy: sort.key, // Envia a chave de ordenação
                    sortOrder: sort.direction, // Envia a direção
                }
            });
            setClients(data.clients);
            setPagination({
                currentPage: data.currentPage,
                totalPages: data.totalPages,
                totalClients: data.totalClients,
            });
        } catch (error) {
            toast.error("Falha ao carregar a lista de clientes.");
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Efeito principal agora depende também da ordenação
    useEffect(() => {
        // Debounce para a busca, para não fazer uma API call a cada letra digitada
        const timer = setTimeout(() => {
            fetchClients(pagination.currentPage, searchTerm, sortConfig);
        }, 500); // Atraso de 500ms

        return () => clearTimeout(timer);
    }, [fetchClients, pagination.currentPage, searchTerm, sortConfig]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    // --- NOVA: FUNÇÃO PARA MUDAR A ORDENAÇÃO ---
    const handleSort = (key) => {
        let direction = 'ASC';
        // Se já estamos ordenando por esta chave e em ordem ascendente, inverte para descendente
        if (sortConfig.key === key && sortConfig.direction === 'ASC') {
            direction = 'DESC';
        }
        setSortConfig({ key, direction });
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Volta para a primeira página
    };

    // Função auxiliar para renderizar o ícone de ordenação
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ASC' ? ' ▲' : ' ▼';
    };

    return (
        <div className="admin-page-container">
            <h1 className="admin-page-title">Clientes</h1>

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    className="search-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                <span>Total de Clientes: {pagination.totalClients}</span>
            </div>

            <div className="clients-table-container">
                <table className="clients-table">
                    <thead>
                    <tr>
                        {/* Adiciona o onClick para os cabeçalhos ordenáveis */}
                        <th onClick={() => handleSort('first_name')}>Nome {getSortIcon('first_name')}</th>
                        <th onClick={() => handleSort('email')}>Email {getSortIcon('email')}</th>
                        <th onClick={() => handleSort('country')}>País {getSortIcon('country')}</th>
                        <th>Contas Deriv</th>
                        <th onClick={() => handleSort('is_vip')}>Status VIP {getSortIcon('is_vip')}</th>
                        <th onClick={() => handleSort('created_at')}>Data de Registro {getSortIcon('created_at')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr><td colSpan="6">Carregando...</td></tr>
                    ) : clients.length > 0 ? (
                        clients.map(client => (
                            <tr key={client.id}>
                                <td data-label="Nome">{`${client.first_name || ''} ${client.last_name || ''}`}</td>
                                <td data-label="Email">{client.email}</td>
                                <td data-label="País">{client.country || 'N/A'}</td>
                                <td data-label="Login ID">{client.deriv_accounts || 'Nenhuma'}</td>
                                <td data-label="Sinais VIP">{client.is_vip ? 'Sim' : 'Não'}</td>
                                <td data-label="Cadastro">{new Date(client.created_at).toLocaleDateString('pt-BR')}</td>
                            </tr>
                        ))
                    ) : (
                        <tr><td colSpan="6">Nenhum cliente encontrado.</td></tr>
                    )}
                    </tbody>
                </table>
            </div>

            <div className="pagination-controls">
                <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1}>
                    Anterior
                </button>
                <span>Página {pagination.currentPage} de {pagination.totalPages}</span>
                <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages}>
                    Próxima
                </button>
            </div>
        </div>
    );
};

export default ClientsPage;